import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

// Mobile-friendly toast notification utility
export const showMobileToast = (message, type = 'success') => {
  // Remove any existing toasts
  const existingToasts = document.querySelectorAll('.mobile-toast');
  existingToasts.forEach(toast => {
    if (document.body.contains(toast)) {
      document.body.removeChild(toast);
    }
  });

  // Create new toast
  const toast = document.createElement('div');
  toast.className = 'mobile-toast';
  toast.textContent = message;
  
  // Add type-specific styling
  if (type === 'success') {
    toast.style.backgroundColor = 'rgba(46, 125, 50, 0.95)';
  } else if (type === 'error') {
    toast.style.backgroundColor = 'rgba(211, 47, 47, 0.95)';
  }
  
  document.body.appendChild(toast);
  
  // Remove toast after animation
  setTimeout(() => {
    if (document.body.contains(toast)) {
      document.body.removeChild(toast);
    }
  }, 3000);
};

// Enhanced clipboard copy function with better mobile feedback
export const copyToClipboardEnhanced = async (text, successMessage = 'Copied!') => {
  try {
    await navigator.clipboard.writeText(text);
    
    // Show mobile-friendly toast notification
    showMobileToast(successMessage);
    
    return true;
  } catch (err) {
    console.error('Failed to copy: ', err);
    
    // Fallback: try to use the older execCommand method
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        showMobileToast(successMessage);
        return true;
      } else {
        throw new Error('execCommand failed');
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed: ', fallbackErr);
      showMobileToast('Failed to copy', 'error');
      return false;
    }
  }
};

// Utility functions for handling links in location fields

/**
 * Detects if text contains a URL
 * @param {string} text - The text to check
 * @returns {boolean} - True if text contains a URL
 */
export const containsUrl = (text) => {
  if (!text) return false;
  
  // Enhanced regex to catch more URL patterns including Google Meet
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|meet\.google\.com\/[^\s]+|[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|io|co|us|uk)\/[^\s]*)/i;
  return urlRegex.test(text);
};

/**
 * Extracts URL from text
 * @param {string} text - The text containing the URL
 * @returns {string|null} - The extracted URL or null if none found
 */
export const extractUrl = (text) => {
  if (!text) return null;
  
  // Enhanced regex to catch more URL patterns
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|meet\.google\.com\/[^\s]+|[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|io|co|us|uk)\/[^\s]*)/i;
  const match = text.match(urlRegex);
  
  if (!match) return null;
  
  let url = match[0];
  
  // Add protocol if missing
  if (url.startsWith('www.') || url.startsWith('meet.google.com') || (!url.startsWith('http') && url.includes('.'))) {
    url = 'https://' + url;
  }
  
  return url;
};

/**
 * Gets a user-friendly display name for a URL
 * @param {string} url - The URL to get display name for
 * @returns {string} - A user-friendly display name
 */
export const getUrlDisplayName = (url) => {
  if (!url) return '';
  
  try {
    // Handle URLs that might not have protocol
    const fullUrl = url.startsWith('http') ? url : 'https://' + url;
    const urlObj = new URL(fullUrl);
    const hostname = urlObj.hostname.toLowerCase();
    
    // Common service mappings - enhanced Google Meet detection
    if (hostname.includes('zoom.us') || hostname.includes('zoom.com')) return 'Join Zoom Meeting';
    if (hostname.includes('teams.microsoft.com') || hostname.includes('teams.live.com')) return 'Join Teams Meeting';
    if (hostname.includes('meet.google.com') || hostname.includes('meet.google') || hostname.includes('googlemeet')) return 'Join Google Meet';
    if (hostname.includes('webex.com') || hostname.includes('webex.cisco.com')) return 'Join Webex Meeting';
    if (hostname.includes('gotomeeting.com') || hostname.includes('gotomeet.me')) return 'Join GoToMeeting';
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) return 'Watch on YouTube';
    if (hostname.includes('facebook.com') || hostname.includes('fb.com') || hostname.includes('fb.me')) return 'View on Facebook';
    if (hostname.includes('instagram.com') || hostname.includes('instagr.am')) return 'View on Instagram';
    if (hostname.includes('twitter.com') || hostname.includes('x.com') || hostname.includes('t.co')) return 'View on Twitter';
    if (hostname.includes('linkedin.com') || hostname.includes('lnkd.in')) return 'View on LinkedIn';
    if (hostname.includes('discord.gg') || hostname.includes('discord.com') || hostname.includes('discordapp.com')) return 'Join Discord';
    if (hostname.includes('twitch.tv')) return 'Watch on Twitch';
    if (hostname.includes('eventbrite.com') || hostname.includes('eventbrite.')) return 'View on Eventbrite';
    if (hostname.includes('calendly.com')) return 'Schedule on Calendly';
    if (hostname.includes('jitsi.meet') || hostname.includes('jitsi.org')) return 'Join Jitsi Meeting';
    if (hostname.includes('whereby.com')) return 'Join Whereby Meeting';
    if (hostname.includes('bluejeans.com')) return 'Join BlueJeans Meeting';
    
    // For other URLs, use the hostname
    return hostname.replace('www.', '');
  } catch (error) {
    // Fallback for invalid URLs - try to extract service name from text
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('meet.google')) return 'Join Google Meet';
    if (lowerUrl.includes('zoom')) return 'Join Zoom Meeting';
    if (lowerUrl.includes('teams')) return 'Join Teams Meeting';
    
    // Final fallback
    return url.length > 30 ? url.substring(0, 30) + '...' : url;
  }
};

/**
 * Renders location text with clickable links and copy button
 * @param {string} location - The location text that may contain URLs
 * @returns {JSX.Element} - React element with clickable links and copy button
 */
export const renderLocationWithLinks = (location) => {
  // Handle null, undefined, or empty strings
  if (!location || typeof location !== 'string' || location.trim() === '') {
    return null;
  }

  // Clean the location text
  const cleanLocation = location.trim();
  
  // Copy to clipboard function with snackbar notification
  const handleCopyToClipboard = (textToCopy, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    copyToClipboardEnhanced(textToCopy, 'Copied to clipboard!')
      .then(success => {
        if (!success) {
          // Fallback: manual copy attempt
          navigator.clipboard.writeText(textToCopy)
            .then(() => {
              showMobileToast('Copied to clipboard!');
            })
            .catch(err => {
              console.error('Failed to copy: ', err);
              alert('Failed to copy text. Please try again.');
            });
        }
      });
  };

  // If location doesn't contain URLs, just show text with copy button
  if (!containsUrl(cleanLocation)) {
    return (
      <span className="location-link-container">
        <span className="location-text">{cleanLocation}</span>
        <button
          className="copy-link-btn"
          onClick={(e) => handleCopyToClipboard(cleanLocation, e)}
          title="Copy location"
          aria-label="Copy location to clipboard"
        >
          <FontAwesomeIcon icon={faCopy} />
        </button>
      </span>
    );
  }

  try {
    // Enhanced URL regex that matches the detection function - includes Google Meet and other patterns
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|meet\.google\.com\/[^\s]+|[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|io|co|us|uk)\/[^\s]*)/gi;
    const matches = cleanLocation.match(urlRegex);
    
    if (!matches || matches.length === 0) {
      // Fallback: treat as plain text with copy button
      return (
        <span className="location-link-container">
          <span className="location-text">{cleanLocation}</span>
          <button
            className="copy-link-btn"
            onClick={(e) => handleCopyToClipboard(cleanLocation, e)}
            title="Copy location"
            aria-label="Copy location to clipboard"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </span>
      );
    }

    // If the location is just a URL (with possible whitespace), show only the display name as a link
    const firstMatch = matches[0];
    const isJustUrl = cleanLocation.replace(/\s+/g, '') === firstMatch.replace(/\s+/g, '');
    
    if (isJustUrl) {
      // Add protocol if missing
      let url = firstMatch;
      if (url.startsWith('www.') || url.startsWith('meet.google.com') || (!url.startsWith('http') && url.includes('.'))) {
        url = 'https://' + url;
      }
      
      const displayName = getUrlDisplayName(url);
      
      return (
        <span className="location-link-container">
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="location-link"
            onClick={(e) => e.stopPropagation()}
          >
            {displayName}
          </a>
          <button
            className="copy-link-btn"
            onClick={(e) => handleCopyToClipboard(url, e)}
            title="Copy link"
            aria-label="Copy link to clipboard"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </span>
      );
    }

    // If location has text + URL(s), replace the URL parts with clickable links and add copy button for entire text
    const parts = cleanLocation.split(urlRegex);
    
    return (
      <span className="location-link-container">
        <span className="location-content">
          {parts.map((part, index) => {
            // Skip empty parts
            if (!part) return null;
            
            if (urlRegex.test(part)) {
              try {
                // Add protocol if missing
                let fullUrl = part;
                if (part.startsWith('www.') || part.startsWith('meet.google.com') || (!part.startsWith('http') && part.includes('.'))) {
                  fullUrl = 'https://' + part;
                }
                
                const displayName = getUrlDisplayName(fullUrl);
                
                return (
                  <a 
                    key={index}
                    href={fullUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="location-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {displayName}
                  </a>
                );
              } catch (error) {
                // If URL processing fails, just return the original text
                console.warn('Failed to process URL:', part);
                return <span key={index}>{part}</span>;
              }
            }
            
            return <span key={index}>{part}</span>;
          }).filter(Boolean)} {/* Remove null entries */}
        </span>
        <button
          className="copy-link-btn"
          onClick={(e) => handleCopyToClipboard(cleanLocation, e)}
          title="Copy location"
          aria-label="Copy location to clipboard"
        >
          <FontAwesomeIcon icon={faCopy} />
        </button>
      </span>
    );
  } catch (error) {
    // If anything fails, fall back to plain text with copy button
    console.warn('Error processing location text:', error);
    return (
      <span className="location-link-container">
        <span className="location-text">{cleanLocation}</span>
        <button
          className="copy-link-btn"
          onClick={(e) => handleCopyToClipboard(cleanLocation, e)}
          title="Copy location"
          aria-label="Copy location to clipboard"
        >
          <FontAwesomeIcon icon={faCopy} />
        </button>
      </span>
    );
  }
};